// =====================================================
// EMAIL API + EXPENSE API + EXPENSE REPORTS API + EXPENSE EMAIL INGEST API +
// VENDOR MAPPINGS API + EXPENSE AGGREGATIONS API + RECURRING EXPENSES API
// =====================================================

import { z } from 'zod';
import { apiFetch } from './api.core';
import {
  EmailSummarySchema,
  ThreadSummarySchema,
  SendEmailRequestSchema,
  ThreadBucketSchema,
  CounterpartyKindSchema,
  LinkThreadRequestSchema,
  EmailExportRequestSchema,
  RecognizedAddressSchema,
  RecognizedAddressListResponseSchema,
} from '@resida/api-types/lease-support';
import { AttachmentSchema } from '@resida/api-types/landlord-chat';
import type {
  Expense,
  ExpenseDetail,
  ExpenseAttachment,
  ExpenseCategory,
  CreateExpenseBody,
  UpdateExpenseBody,
  ListExpensesParams,
  YearEndReport,
  EmailIngestAddress,
  AggregationListItem,
  AggregationDetail,
  CreateAggregationBody,
  UpdateAggregationBody,
  RecurringExpense,
  RecurringExpenseDetail,
  CreateRecurringExpenseBody,
  UpdateRecurringExpenseBody,
  VendorPropertyMapping,
} from '../types/expense.types';

export type { Expense, ExpenseDetail, ExpenseAttachment, ExpenseCategory, CreateExpenseBody, UpdateExpenseBody, ListExpensesParams, YearEndReport, EmailIngestAddress, AggregationListItem, AggregationDetail, CreateAggregationBody, UpdateAggregationBody, RecurringExpense, RecurringExpenseDetail, CreateRecurringExpenseBody, UpdateRecurringExpenseBody, VendorPropertyMapping };

// =====================================================
// EMAIL API
// =====================================================

// Derived from EmailSummarySchema in @resida/api-types/lease-support
export type EmailMessage = z.infer<typeof EmailSummarySchema>;

// Derived from ThreadSummarySchema in @resida/api-types/lease-support
export type ThreadSummary = z.infer<typeof ThreadSummarySchema>;

// Derived from AttachmentSchema in @resida/api-types/landlord-chat (same schema as ChatAttachment)
export type EmailAttachment = z.infer<typeof AttachmentSchema>;

// Derived from SendEmailRequestSchema in @resida/api-types/lease-support
export type SendEmailInput = z.infer<typeof SendEmailRequestSchema>;

// Derived from ThreadBucketSchema in @resida/api-types/lease-support — 'matched' (default,
// filed under a tenant or vendor) or 'other' (UNMATCHED, the "Other senders" view).
export type ThreadBucket = z.infer<typeof ThreadBucketSchema>;

// Derived from CounterpartyKindSchema in @resida/api-types/lease-support
export type CounterpartyKind = z.infer<typeof CounterpartyKindSchema>;

// Derived from LinkThreadRequestSchema in @resida/api-types/lease-support
export type LinkThreadRequest = z.infer<typeof LinkThreadRequestSchema>;

// Derived from EmailExportRequestSchema in @resida/api-types/lease-support
export type EmailExportRequest = z.infer<typeof EmailExportRequestSchema>;

// Derived from RecognizedAddressSchema in @resida/api-types/lease-support
export type RecognizedAddress = z.infer<typeof RecognizedAddressSchema>;

// Derived from RecognizedAddressListResponseSchema in @resida/api-types/lease-support
export type RecognizedAddressList = z.infer<typeof RecognizedAddressListResponseSchema>;

export interface ThreadListFilters {
  counterpartyType?: CounterpartyKind;
  vendorId?: string;
}

/**
 * List email threads (one entry per conversation): 'matched' (filed under a tenant or
 * vendor, default) or 'other' (UNMATCHED — the "Other senders" view; BR-94/BR-95),
 * optionally narrowed to one counterparty type or vendor (BR-117).
 */
export async function listThreads(
  bucket: ThreadBucket = 'matched',
  filters: ThreadListFilters = {}
): Promise<{ threads: ThreadSummary[] }> {
  const params = new URLSearchParams({ bucket });
  if (filters.counterpartyType) params.set('counterpartyType', filters.counterpartyType);
  if (filters.vendorId) params.set('vendorId', filters.vendorId);
  return apiFetch<{ threads: ThreadSummary[] }>(`/landlord/api/email/threads?${params.toString()}`);
}

/**
 * File an "Other senders" thread under a tenant, a vendor, or a new vendor created on the
 * spot (BR-114, BR-126). The sender's address is always recognized afterwards.
 */
export async function linkThread(threadId: string, body: LinkThreadRequest): Promise<ThreadSummary> {
  return apiFetch<ThreadSummary>(`/landlord/api/email/threads/${threadId}/link`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Discard an "Other senders" thread (BR-115).
 */
export async function discardThread(threadId: string): Promise<ThreadSummary> {
  return apiFetch<ThreadSummary>(`/landlord/api/email/threads/${threadId}/discard`, {
    method: 'POST',
  });
}

function counterpartyAddressesPath(kind: CounterpartyKind, id: string): string {
  return `/landlord/api/${kind === 'tenant' ? 'tenants' : 'vendors'}/${id}/alternate-emails`;
}

/**
 * A tenant's or vendor's primary address plus the extra recognized addresses (BR-98)
 */
export async function listCounterpartyAddresses(
  kind: CounterpartyKind,
  id: string
): Promise<RecognizedAddressList> {
  return apiFetch<RecognizedAddressList>(counterpartyAddressesPath(kind, id));
}

/**
 * Remove a recognized address — future mail from it no longer auto-matches this counterparty.
 */
export async function removeCounterpartyAddress(
  kind: CounterpartyKind,
  id: string,
  addressId: string
): Promise<void> {
  return apiFetch<void>(`${counterpartyAddressesPath(kind, id)}/${addressId}`, { method: 'DELETE' });
}

/**
 * Promote a recognized address to primary; the old primary becomes a recognized address.
 */
export async function makeCounterpartyAddressPrimary(
  kind: CounterpartyKind,
  id: string,
  addressId: string
): Promise<void> {
  return apiFetch<void>(`${counterpartyAddressesPath(kind, id)}/${addressId}/make-primary`, {
    method: 'POST',
  });
}

/**
 * Get all messages in a thread (marks inbound as read)
 */
export async function getThreadMessages(threadId: string): Promise<{ messages: EmailMessage[] }> {
  return apiFetch<{ messages: EmailMessage[] }>(`/landlord/api/email/threads/${threadId}/messages`);
}

/**
 * Reply to a thread
 */
export async function replyToThread(threadId: string, bodyText: string): Promise<ThreadSummary> {
  return apiFetch<ThreadSummary>(`/landlord/api/email/threads/${threadId}/reply`, {
    method: 'POST',
    body: JSON.stringify({ bodyText }),
  });
}

/**
 * Send email to one or more tenants and/or vendors (one thread, one delivery each; BR-116)
 */
export async function sendEmail(data: SendEmailInput): Promise<ThreadSummary> {
  return apiFetch<ThreadSummary>('/landlord/api/email/messages', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Download email attachment
 */
export async function downloadEmailAttachment(id: string): Promise<Response> {
  return fetch(`/landlord/api/email/attachments/${id}/download`, {
    credentials: 'include',
  });
}

/**
 * Get this landlord's messaging "From" address (what tenants and vendors reply to)
 */
export async function getMyEmailAddress(): Promise<{ address: string }> {
  return apiFetch<{ address: string }>('/landlord/api/email/my-address');
}

/**
 * Export email history
 */
export async function exportEmailHistory(params: EmailExportRequest = {}): Promise<Response> {
  return fetch('/landlord/api/email/export', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(params),
  });
}

// =====================================================
// EXPENSE API
// =====================================================

export const expensesApi = {
  /**
   * List expenses for a property (or unit within a property)
   * Response: { expenses: Expense[] }
   */
  list: (params: ListExpensesParams): Promise<{ expenses: Expense[] }> => {
    const searchParams = new URLSearchParams();
    if (params.propertyId) searchParams.set('propertyId', params.propertyId);
    if (params.unitId) searchParams.set('unitId', params.unitId);
    if (params.startDate) searchParams.set('startDate', params.startDate);
    if (params.endDate) searchParams.set('endDate', params.endDate);
    if (params.categoryId) searchParams.set('categoryId', params.categoryId);
    if (params.sourceType) searchParams.set('sourceType', params.sourceType);
    if (params.status) searchParams.set('status', params.status);
    const qs = searchParams.toString();
    return apiFetch<{ expenses: Expense[] }>(`/landlord/api/expenses${qs ? `?${qs}` : ''}`);
  },

  /**
   * Get a single expense with attachments and category detail
   * Response: ExpenseDetail (direct, unwrapped)
   */
  get: (id: string): Promise<ExpenseDetail> =>
    apiFetch<ExpenseDetail>(`/landlord/api/expenses/${id}`),

  /**
   * Create a manual expense
   * Response: ExpenseDetail (direct, unwrapped)
   */
  create: (body: CreateExpenseBody): Promise<ExpenseDetail> =>
    apiFetch<ExpenseDetail>('/landlord/api/expenses', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /**
   * Update an expense
   * Response: ExpenseDetail (direct, unwrapped)
   */
  update: (id: string, body: UpdateExpenseBody): Promise<ExpenseDetail> =>
    apiFetch<ExpenseDetail>(`/landlord/api/expenses/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  /**
   * Delete an expense
   */
  delete: (id: string): Promise<void> =>
    apiFetch<void>(`/landlord/api/expenses/${id}`, { method: 'DELETE' }),

  /**
   * Upload a file to create an expense (multipart)
   * Response: ExpenseDetail (direct, unwrapped)
   */
  upload: (formData: FormData): Promise<ExpenseDetail> =>
    apiFetch<ExpenseDetail>('/landlord/api/expenses/upload', {
      method: 'POST',
      body: formData,
    }),

  /**
   * Add attachment to an existing expense (multipart)
   * Response: ExpenseAttachment (direct, unwrapped)
   */
  addAttachment: (expenseId: string, formData: FormData): Promise<ExpenseAttachment> =>
    apiFetch<ExpenseAttachment>(`/landlord/api/expenses/${expenseId}/attachments`, {
      method: 'POST',
      body: formData,
    }),

  /**
   * Delete an attachment from an expense
   */
  deleteAttachment: (expenseId: string, attachmentId: string): Promise<void> =>
    apiFetch<void>(`/landlord/api/expenses/${expenseId}/attachments/${attachmentId}`, {
      method: 'DELETE',
    }),

  /**
   * Re-run AI categorization for an expense
   * Response: ExpenseDetail (direct, unwrapped)
   */
  recategorize: (id: string): Promise<ExpenseDetail> =>
    apiFetch<ExpenseDetail>(`/landlord/api/expenses/${id}/categorize`, {
      method: 'POST',
    }),

  /**
   * Get attachment download URL (direct URL for binary stream)
   */
  attachmentDownloadUrl: (expenseId: string, attachmentId: string): string =>
    `/landlord/api/expenses/${expenseId}/attachments/${attachmentId}/download`,

  /**
   * Get all PENDING_REVIEW expenses for the review queue
   * Response: { expenses: Expense[] }
   */
  getReviewQueue: (): Promise<{ expenses: Expense[] }> =>
    apiFetch<{ expenses: Expense[] }>('/landlord/api/expenses/review-queue'),

  /**
   * Confirm an expense (with optional field corrections)
   * Response: ExpenseDetail (direct, unwrapped)
   */
  confirm: (id: string, corrections?: {
    propertyId?: string;
    unitId?: string | null;
    scope?: 'PROPERTY' | 'UNIT';
    amount?: number;
    expenseDate?: string;
    description?: string;
    vendorName?: string | null;
    irsCategoryId?: string | null;
  }): Promise<ExpenseDetail> =>
    apiFetch<ExpenseDetail>(`/landlord/api/expenses/${id}/confirm`, {
      method: 'POST',
      body: JSON.stringify(corrections ?? {}),
    }),

  /**
   * Bulk-confirm multiple expenses
   * Response: { confirmedCount: number }
   */
  bulkConfirm: (expenseIds: string[]): Promise<{ confirmedCount: number }> =>
    apiFetch<{ confirmedCount: number }>('/landlord/api/expenses/review-queue/bulk-confirm', {
      method: 'POST',
      body: JSON.stringify({ expenseIds }),
    }),

  /**
   * Get source email content for an email-ingested expense
   * Response: { email: { from, subject, date, body } }
   */
  getSourceEmailContent: (id: string): Promise<{
    email: { from: string; subject: string; date: string | null; body: string };
  }> =>
    apiFetch<{ email: { from: string; subject: string; date: string | null; body: string } }>(
      `/landlord/api/expenses/${id}/source-email`
    ),
};

export const expenseCategoriesApi = {
  /**
   * List all IRS expense categories
   */
  list: (): Promise<{ categories: ExpenseCategory[] }> =>
    apiFetch<{ categories: ExpenseCategory[] }>('/landlord/api/expense-categories'),
};

// =====================================================
// EXPENSE REPORTS API
// =====================================================

export const expenseReportsApi = {
  /**
   * Get year-end Schedule E report for a property
   * Response: YearEndReport (direct, unwrapped)
   */
  yearEnd: (params: { year: number; propertyId: string }): Promise<YearEndReport> => {
    const searchParams = new URLSearchParams({
      year: String(params.year),
      propertyId: params.propertyId,
    });
    return apiFetch<YearEndReport>(`/landlord/api/expense-reports/year-end?${searchParams.toString()}`);
  },

  /**
   * Export expenses as CSV for a date range and property
   * Response: { csv: string } — caller triggers browser download
   */
  export: (params: { startDate: string; endDate: string; propertyId: string }): Promise<{ csv: string }> => {
    const searchParams = new URLSearchParams({
      startDate: params.startDate,
      endDate: params.endDate,
      propertyId: params.propertyId,
    });
    return apiFetch<{ csv: string }>(`/landlord/api/expense-reports/export?${searchParams.toString()}`);
  },
};

// =====================================================
// EXPENSE EMAIL INGEST API
// =====================================================

export const expenseEmailApi = {
  /**
   * Get the current manager's email ingest address (or null if not created)
   * Response: { address: EmailIngestAddress | null }
   */
  get: (): Promise<{ address: EmailIngestAddress | null }> =>
    apiFetch<{ address: EmailIngestAddress | null }>('/landlord/api/expense-email'),

  /**
   * Create a new email ingest address
   * Response: EmailIngestAddress (direct, unwrapped)
   */
  create: (body: { defaultPropertyId?: string }): Promise<EmailIngestAddress> =>
    apiFetch<EmailIngestAddress>('/landlord/api/expense-email', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /**
   * Update the email ingest address settings
   * Response: EmailIngestAddress (direct, unwrapped)
   */
  update: (body: { defaultPropertyId?: string | null; isActive?: boolean }): Promise<EmailIngestAddress> =>
    apiFetch<EmailIngestAddress>('/landlord/api/expense-email', {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
};

// =====================================================
// EXPENSE AGGREGATIONS API
// =====================================================

export const aggregationsApi = {
  /**
   * List aggregations, optionally scoped to a property
   * Response: { aggregations: AggregationListItem[] }
   */
  list: (propertyId?: string, startDate?: string, endDate?: string): Promise<{ aggregations: AggregationListItem[] }> => {
    const params = new URLSearchParams();
    if (propertyId) params.set('propertyId', propertyId);
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    const qs = params.toString();
    const url = qs ? `/landlord/api/expense-aggregations?${qs}` : '/landlord/api/expense-aggregations';
    return apiFetch<{ aggregations: AggregationListItem[] }>(url);
  },

  /**
   * Get a single aggregation with member expenses
   * Response: AggregationDetail (direct, unwrapped)
   */
  get: (id: string): Promise<AggregationDetail> =>
    apiFetch<AggregationDetail>(`/landlord/api/expense-aggregations/${id}`),

  /**
   * Create a new aggregation
   * Response: AggregationDetail (direct, unwrapped)
   */
  create: (body: CreateAggregationBody): Promise<AggregationDetail> =>
    apiFetch<AggregationDetail>('/landlord/api/expense-aggregations', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /**
   * Update aggregation metadata
   * Response: AggregationDetail (direct, unwrapped)
   */
  update: (id: string, body: UpdateAggregationBody): Promise<AggregationDetail> =>
    apiFetch<AggregationDetail>(`/landlord/api/expense-aggregations/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  /**
   * Delete an aggregation (unlinks member expenses, does NOT delete them)
   */
  delete: (id: string): Promise<void> =>
    apiFetch<void>(`/landlord/api/expense-aggregations/${id}`, { method: 'DELETE' }),

  /**
   * Add expenses to an aggregation
   * Response: AggregationDetail (direct, unwrapped)
   */
  addExpenses: (id: string, expenseIds: string[]): Promise<AggregationDetail> =>
    apiFetch<AggregationDetail>(`/landlord/api/expense-aggregations/${id}/expenses`, {
      method: 'POST',
      body: JSON.stringify({ expenseIds }),
    }),

  /**
   * Remove an expense from an aggregation
   */
  removeExpense: (id: string, expenseId: string): Promise<void> =>
    apiFetch<void>(`/landlord/api/expense-aggregations/${id}/expenses/${expenseId}`, {
      method: 'DELETE',
    }),

  /**
   * Trigger AI classification for an aggregation
   * Response: AggregationDetail (direct, unwrapped)
   */
  classify: (id: string): Promise<AggregationDetail> =>
    apiFetch<AggregationDetail>(`/landlord/api/expense-aggregations/${id}/classify`, {
      method: 'POST',
    }),

  /**
   * Manually set the classification type for an aggregation
   * Response: AggregationDetail (direct, unwrapped)
   */
  setClassification: (
    id: string,
    aggregationType: 'capital_improvement' | 'repair' | 'move_out' | 'other'
  ): Promise<AggregationDetail> =>
    apiFetch<AggregationDetail>(`/landlord/api/expense-aggregations/${id}/classification`, {
      method: 'PATCH',
      body: JSON.stringify({ aggregationType }),
    }),
};

// =====================================================
// RECURRING EXPENSES API
// =====================================================

export const recurringExpensesApi = {
  /**
   * List recurring expense templates for a property
   * Response: { templates: RecurringExpense[] }
   */
  list: (propertyId?: string): Promise<{ templates: RecurringExpense[] }> => {
    const url = propertyId
      ? `/landlord/api/recurring-expenses?propertyId=${encodeURIComponent(propertyId)}`
      : '/landlord/api/recurring-expenses';
    return apiFetch<{ templates: RecurringExpense[] }>(url);
  },

  /**
   * Get a single recurring expense template with generated expense history
   * Response: RecurringExpenseDetail (direct, unwrapped)
   */
  get: (id: string): Promise<RecurringExpenseDetail> =>
    apiFetch<RecurringExpenseDetail>(`/landlord/api/recurring-expenses/${id}`),

  /**
   * Create a new recurring expense template
   * Response: RecurringExpense (direct, unwrapped)
   */
  create: (body: CreateRecurringExpenseBody): Promise<RecurringExpense> =>
    apiFetch<RecurringExpense>('/landlord/api/recurring-expenses', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /**
   * Update a recurring expense template
   * Response: RecurringExpense (direct, unwrapped)
   */
  update: (id: string, body: UpdateRecurringExpenseBody): Promise<RecurringExpense> =>
    apiFetch<RecurringExpense>(`/landlord/api/recurring-expenses/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  /**
   * Pause an active recurring expense template (ACTIVE -> PAUSED)
   * Response: RecurringExpense (direct, unwrapped)
   */
  pause: (id: string): Promise<RecurringExpense> =>
    apiFetch<RecurringExpense>(`/landlord/api/recurring-expenses/${id}/pause`, {
      method: 'POST',
    }),

  /**
   * Resume a paused recurring expense template (PAUSED -> ACTIVE)
   * Response: RecurringExpense (direct, unwrapped)
   */
  resume: (id: string): Promise<RecurringExpense> =>
    apiFetch<RecurringExpense>(`/landlord/api/recurring-expenses/${id}/resume`, {
      method: 'POST',
    }),

  /**
   * Cancel a recurring expense template (terminal state)
   * Response: RecurringExpense (direct, unwrapped)
   */
  cancel: (id: string): Promise<RecurringExpense> =>
    apiFetch<RecurringExpense>(`/landlord/api/recurring-expenses/${id}/cancel`, {
      method: 'POST',
    }),
};
